<app-intro-container>
  <app-section sectionTitle="WHAT IS THIS TOOL?">
    <p>The Craft Calculator helps you plan profitable crafting runs in Albion Online. Figure out what to craft, how much it'll cost you, and whether it's actually worth your time and silver.</p>
    <p>Perfect for solo crafters looking to turn a profit or guilds planning large production runs. Get the numbers you need to craft smart, not hard.</p>
  </app-section>

  <app-section sectionTitle="KEY FEATURES">
    <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 24px; margin-top: 16px">
      <app-feature featureTitle="🛠️ Multi-Item Crafting Setup">
        <p>Add and configure multiple items for crafting in a single session. Compare different craft groups and select the most profitable items to produce.</p>
      </app-feature>
      <app-feature featureTitle="💰 Real-Time Price Integration">
        <p>Automatically fetch current market prices from AODP or manually set your own prices. Calculate accurate profit margins based on live market data.</p>
      </app-feature>
      <app-feature featureTitle="📋 Smart Shopping Lists">
        <p>Generate organized shopping lists grouped by location with total weight calculations. Know exactly what to buy and where to buy it most efficiently.</p>
      </app-feature>
      <app-feature featureTitle="⚙️ Crafting Specs Management">
        <p>Configure and save your crafting specialization levels to get accurate resource return rates and focus efficiency bonuses in your calculations.</p>
      </app-feature>
      <app-feature featureTitle="💾 Save & Load Configurations">
        <p>Save your craft groups and load them later. Share configurations with guild members or keep multiple setups for different crafting strategies.</p>
      </app-feature>
    </div>
  </app-section>

  <app-section sectionTitle="WHERE'S THE OLD CRAFT CALCULATOR?">
    <p>
      If you want, you can still access the <a routerLink="/crafting-old">old Craft Calculator</a> and check <a routerLink="/crafts">your old craft history</a>. However, we recommend using this new version for a more streamlined and
      feature-rich experience.
    </p>
  </app-section>

  <app-section sectionTitle="HOW TO USE">
    <app-section-subtitle>Getting Started</app-section-subtitle>
    <app-subsection subSectionTitle="Load & Save Tab">
      <p>Start by giving your craft group a name and either load an existing configuration or create a new one. You can save your progress at any time and load previous configurations to continue your work.</p>
    </app-subsection>
    <app-subsection subSectionTitle="Setup Tab">
      <p>This is where you configure your crafting operation:</p>
      <ul>
        <li><strong>Choose Craft Groups:</strong> Select the type of items you want to craft (weapons, armor, consumables, etc.)</li>
        <li><strong>Filter by Tiers:</strong> Use tier filters to focus on specific item tiers that match your crafting capabilities</li>
        <li><strong>Select Items:</strong> Mark the specific items you want to craft in this session</li>
        <li><strong>Set Quantities:</strong> Configure how many of each item you plan to craft</li>
      </ul>
    </app-subsection>

    <app-section-subtitle>Price Management</app-section-subtitle>
    <app-subsection subSectionTitle="Inputs Tab">
      <p>Manage the prices of raw materials and resources needed for crafting:</p>
      <ul>
        <li><strong>Auto-Refresh:</strong> Automatically update prices from AODP with current market data</li>
        <li><strong>Manual Override:</strong> Set custom prices for materials when you have better deals or bulk purchases</li>
        <li><strong>Buy Locations:</strong> Configure where you plan to purchase each material for accurate transport planning</li>
      </ul>
    </app-subsection>
    <app-subsection subSectionTitle="Outputs Tab">
      <p>Set pricing strategies for your finished products:</p>
      <ul>
        <li><strong>Market Prices:</strong> Pull current market data or manually set target prices based on your analysis</li>
        <li><strong>Sell Locations:</strong> Choose where to sell each item for maximum profit</li>
        <li><strong>Price Filtering:</strong> Filter items by missing prices to quickly identify what needs attention</li>
        <li><strong>Bulk Updates:</strong> Set all output prices to current market rates with one click</li>
      </ul>
    </app-subsection>

    <app-section-subtitle>Analysis & Optimization</app-section-subtitle>
    <app-subsection subSectionTitle="Specs Tab">
      <p>Input your character's crafting specializations for precise calculations:</p>
      <ul>
        <li><strong>Specialization Levels:</strong> Enter your current spec levels for each relevant crafting tree</li>
        <li><strong>Resource Returns:</strong> Higher specs mean better resource return rates, reducing your actual material costs</li>
        <li><strong>Focus Efficiency:</strong> Calculate how much focus you'll need and whether it's worth using</li>
        <li><strong>Save/Load Profiles:</strong> Store multiple spec configurations for different characters or builds</li>
      </ul>
    </app-subsection>
    <app-subsection subSectionTitle="Results Tab">
      <p>Get detailed profitability analysis for each craft you've selected:</p>
      <ul>
        <li><strong>Step-by-Step Breakdown:</strong> See exactly what goes into each craft and what comes out</li>
        <li><strong>Cost Analysis:</strong> Material costs, crafting fees, and transport expenses all calculated</li>
        <li><strong>Profit Per Item:</strong> Clear profit margins with percentage returns on your investment</li>
        <li><strong>Focus Calculations:</strong> See how focus usage affects your bottom line</li>
        <li><strong>Total Investment:</strong> Know exactly how much silver you need to start crafting</li>
      </ul>
    </app-subsection>

    <app-section-subtitle>Planning Your Crafting Run</app-section-subtitle>
    <app-subsection subSectionTitle="Shopping List Tab">
      <p>Organized lists that make material gathering efficient:</p>
      <ul>
        <li><strong>Location-Based Groups:</strong> All materials you need to buy in each city, sorted logically</li>
        <li><strong>Weight Tracking:</strong> Know how much carrying capacity you'll need for each trip</li>
        <li><strong>Quantity Totals:</strong> Exact amounts needed, accounting for your specialization returns</li>
        <li><strong>Price Estimates:</strong> Budget planning with current or custom material prices</li>
      </ul>
    </app-subsection>
    <app-subsection subSectionTitle="Selling List Tab">
      <p>Maximize your profits with strategic selling plans:</p>
      <ul>
        <li><strong>Market Optimization:</strong> Items grouped by their best selling locations</li>
        <li><strong>Transport Planning:</strong> Consider carrying capacity and travel distance</li>
        <li><strong>Revenue Tracking:</strong> Expected income from each location</li>
        <li><strong>Profit Margins:</strong> Quick reference for which items make the most money</li>
      </ul>
    </app-subsection>

    <app-section-subtitle>Tips for Success</app-section-subtitle>
    <app-subsection subSectionTitle="Making the Most of the Calculator">
      <ul>
        <li><strong>Start Small:</strong> Test the calculator with a few items before planning large crafting operations</li>
        <li><strong>Update Regularly:</strong> Refresh prices frequently as Albion's market moves fast</li>
        <li><strong>Consider All Costs:</strong> Factor in travel time, transport fees, and opportunity costs</li>
        <li><strong>Focus Strategy:</strong> Use focus on high-value items where the efficiency bonus makes the biggest difference</li>
        <li><strong>Market Research:</strong> Check multiple cities and time your crafting when conditions are favorable</li>
        <li><strong>Save Configurations:</strong> Keep successful setups saved so you can repeat profitable runs</li>
      </ul>
    </app-subsection>
  </app-section>
</app-intro-container>
